/**
 * Career value-card catalog — the deck for Workshop 1 »Werte-Kartenspiel« (§3).
 *
 * Mapping status: SIGNED OFF 2026-07-23 (operator, after 9-model panel adjudication —
 * see docs/w1-schwartz-mapping-review.md and docs/reports/2026-07-23-schwartz-panel-report.html).
 * Panel result: 17/18 contested mappings confirmed; one correction adopted
 * (hingabe: achievement → benevolence primary). Downstream slices (V2+) may now
 * build on the Schwartz/Big-Five annotation; any future change needs a fresh
 * sign-off AND a fingerprint re-pin in the catalog test.
 *
 * PROVENANCE (content only, no import): the 62 German value words and their four
 * colour families are PORTED as career-owned data from the proven DNA deck
 *   orgs/uneq/dna-workshop/values/explore_your_values/values_table.md
 * The words are generic German value language, so the brand invariant holds
 * (CLAUDE.md »No client brand«): this file imports NOTHING from DNA, carries NO
 * `uneq:` ids, and re-slugs every card to a career-native `cardId`. The DNA path
 * above is recorded for provenance ONLY.
 *
 * The Schwartz 4+10 annotation and the Big-Five working-style tags are AUTHORED
 * here (a verify-then-author research pass — a search of the DNA tree found no
 * Schwartz/higher-order annotation, only the 4 colour families; redesign §3 note,
 * decisions Q1). They are frozen catalog DATA once signed off, not model output.
 *
 * `cardId` is the CROSS-SUBJECT COMPARABILITY KEY (§9): lower-cased, umlaut-
 * transliterated, and FROZEN once shipped — a rename strands every stored pyramid.
 * The `cardId === slugifyValueName(name)` invariant is enforced by the catalog
 * test, so a card can never drift from its slug silently.
 */

// ─── Vocabulary tokens (career-native literal types — NOT domain-type mirrors) ──

/** Career-native colour-family tokens (»Blau/Grün/Gelb/Rot« as ascii slugs). */
export type ColourFamily = "blau" | "gruen" | "gelb" | "rot"

/** Schwartz higher-order dimension (4) — the stored top level (§5a). */
export type SchwartzHigherOrder =
  | "conservation"
  | "self_transcendence"
  | "openness_to_change"
  | "self_enhancement"

/** Schwartz basic value type (10) — the stored second level (§5a). */
export type SchwartzBasicValue =
  | "security"
  | "conformity"
  | "tradition"
  | "benevolence"
  | "universalism"
  | "self_direction"
  | "stimulation"
  | "hedonism"
  | "achievement"
  | "power"

/** Big-Five working-style trait (positive-framed OCEAN tokens, §5a / Q4). */
export type BigFiveTrait =
  | "openness"
  | "conscientiousness"
  | "extraversion"
  | "agreeableness"
  | "emotional_stability"

/** Big-Five polarity — the pole a working-style signal loads on. */
export type BigFivePole = "high" | "low"

/** A working-style tag on a card (only where the card carries a CLEAR signal, Q4). */
export interface BigFiveSignal {
  readonly trait: BigFiveTrait
  readonly pole: BigFivePole
}

/** One value card — the frozen catalog entry. */
export interface ValueCard {
  /** Stable comparability key (§9) — `slugifyValueName(name)`, frozen once shipped. */
  readonly cardId: string
  /** The German value word (the card face). */
  readonly name: string
  /** Career-native colour family (the deck's four buckets). */
  readonly colour: ColourFamily
  /** Schwartz higher-order dimension (the stored top level). */
  readonly higherOrder: SchwartzHigherOrder
  /** Schwartz basic value type(s) — the primary first; 1, max 2 (§3). */
  readonly schwartz: readonly SchwartzBasicValue[]
  /** Big-Five working-style signal(s) — absent on most cards (don't force coverage, Q4). */
  readonly bigFive?: readonly BigFiveSignal[]
  /** True when `higherOrder` deviates from the card's colour-family prior (§5a). */
  readonly deviatesFromColourPrior?: boolean
  /** True when the operator should look here first — a genuinely contestable assignment. */
  readonly contestable?: boolean
  /** Short German rationale — present where the assignment is non-obvious. */
  readonly rationale?: string
}

/** Colour-family definition — German label + the higher-order-dimension prior (§5a). */
export interface ColourFamilyDef {
  readonly id: ColourFamily
  /** Candidate-facing German label. */
  readonly label: string
  /** The higher-order-dimension PRIOR for this family (a prior, not a law, §5a). */
  readonly higherOrderPrior: SchwartzHigherOrder
}

// ─── Slug seam (local, umlaut-transliterated — mirrors the established convention) ──

/**
 * Deterministic career-native slug: lower-case, umlauts transliterated
 * (ä→ae, ö→oe, ü→ue, ß→ss), everything else collapsed to single hyphens. The
 * catalog words are single tokens, so slugs are single words in practice; the
 * hyphen collapse keeps the function total for any future multi-word card.
 */
export function slugifyValueName(name: string): string {
  return name
    .toLowerCase()
    .replaceAll("ä", "ae")
    .replaceAll("ö", "oe")
    .replaceAll("ü", "ue")
    .replaceAll("ß", "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

// ─── Colour families (Blau/Grün/Gelb/Rot → higher-order-dimension priors, §5a) ──

export const VALUE_COLOUR_FAMILIES: readonly ColourFamilyDef[] = [
  { id: "blau", label: "Ordnung · Sicherheit", higherOrderPrior: "conservation" },
  { id: "gruen", label: "Beziehungen · Gemeinschaft", higherOrderPrior: "self_transcendence" },
  { id: "gelb", label: "Erfüllung · Freiheit", higherOrderPrior: "openness_to_change" },
  { id: "rot", label: "Fortschritt · Status", higherOrderPrior: "self_enhancement" },
]

// ─── The 62 cards ────────────────────────────────────────────────────────────
//
// Ordered by colour family, then by the source table's row order (Blau 15 · Grün
// 15 · Gelb 16 · Rot 16 = 62). `schwartz[0]` is the PRIMARY basic value; a second
// entry is a genuine secondary load. `deviatesFromColourPrior` flags where the
// assignment leaves the colour's prior higher-order dimension; `contestable` marks
// the assignments the operator should scrutinise first (see the review doc).

export const VALUE_CARD_CATALOG: readonly ValueCard[] = [
  // ── Blau · Ordnung / Sicherheit (prior: conservation) ──
  { cardId: "tradition", name: "Tradition", colour: "blau", higherOrder: "conservation", schwartz: ["tradition"] },
  {
    cardId: "zuverlaessigkeit", name: "Zuverlässigkeit", colour: "blau",
    higherOrder: "conservation", schwartz: ["conformity", "security"],
    bigFive: [{ trait: "conscientiousness", pole: "high" }],
    rationale: "Verlässlichkeit = Erfüllen von Erwartungen/Verpflichtungen (Konformität) und Stabilität geben (Sicherheit).",
  },
  {
    cardId: "ehrlichkeit", name: "Ehrlichkeit", colour: "blau",
    higherOrder: "self_transcendence", schwartz: ["benevolence"],
    deviatesFromColourPrior: true,
    rationale: "Ehrlichkeit lädt in Schwartz auf Benevolenz (verlässlich/aufrichtig im Umgang) — weicht von der blauen Prior (Conservation) ab.",
  },
  {
    cardId: "disziplin", name: "Disziplin", colour: "blau",
    higherOrder: "conservation", schwartz: ["conformity"],
    bigFive: [{ trait: "conscientiousness", pole: "high" }],
    rationale: "Selbstdisziplin ist ein Konformitäts-Item.",
  },
  { cardId: "sparsamkeit", name: "Sparsamkeit", colour: "blau", higherOrder: "conservation", schwartz: ["security"] },
  { cardId: "gesundheit", name: "Gesundheit", colour: "blau", higherOrder: "conservation", schwartz: ["security"] },
  { cardId: "sicherheit", name: "Sicherheit", colour: "blau", higherOrder: "conservation", schwartz: ["security"] },
  {
    cardId: "achtsamkeit", name: "Achtsamkeit", colour: "blau",
    higherOrder: "conservation", schwartz: ["security"],
    bigFive: [{ trait: "emotional_stability", pole: "high" }],
    contestable: true,
    rationale: "Achtsamkeit als innere Ruhe/Präsenz → Sicherheit (innere Stabilität); vertretbar strittig (auch Selbstbestimmung/Universalismus denkbar).",
  },
  {
    cardId: "bildung", name: "Bildung", colour: "blau",
    higherOrder: "openness_to_change", schwartz: ["self_direction", "achievement"],
    bigFive: [{ trait: "openness", pole: "high" }],
    deviatesFromColourPrior: true, contestable: true,
    rationale: "Bildung als Selbstkultivierung/Neugier → Selbstbestimmung; sekundär Leistung (Kompetenz). Weicht von Blau ab; strittig.",
  },
  {
    cardId: "loyalitaet", name: "Loyalität", colour: "blau",
    higherOrder: "self_transcendence", schwartz: ["benevolence"],
    deviatesFromColourPrior: true,
    rationale: "Loyalität/Treue zur Gruppe ist ein Benevolenz-Item — Abweichung von Blau.",
  },
  { cardId: "glaube", name: "Glaube", colour: "blau", higherOrder: "conservation", schwartz: ["tradition"] },
  {
    cardId: "ruhe", name: "Ruhe", colour: "blau",
    higherOrder: "conservation", schwartz: ["security"],
    bigFive: [{ trait: "emotional_stability", pole: "high" }],
  },
  {
    cardId: "integritaet", name: "Integrität", colour: "blau",
    higherOrder: "openness_to_change", schwartz: ["self_direction", "benevolence"],
    deviatesFromColourPrior: true, contestable: true,
    rationale: "Integrität als prinzipientreue Selbstkonsistenz → Selbstbestimmung; sekundär Benevolenz (Aufrichtigkeit). Abweichung von Blau; strittig.",
  },
  {
    cardId: "ordnung", name: "Ordnung", colour: "blau",
    higherOrder: "conservation", schwartz: ["security", "conformity"],
    bigFive: [{ trait: "conscientiousness", pole: "high" }],
    rationale: "'Soziale Ordnung' ist ein Sicherheits-Item; Strukturbedürfnis grenzt an Konformität.",
  },
  {
    cardId: "gerechtigkeit", name: "Gerechtigkeit", colour: "blau",
    higherOrder: "self_transcendence", schwartz: ["universalism"],
    deviatesFromColourPrior: true,
    rationale: "Soziale Gerechtigkeit/Gleichheit = Universalismus — Abweichung von Blau.",
  },

  // ── Grün · Beziehungen / Gemeinschaft (prior: self_transcendence) ──
  {
    cardId: "ruecksicht", name: "Rücksicht", colour: "gruen",
    higherOrder: "self_transcendence", schwartz: ["benevolence"],
    bigFive: [{ trait: "agreeableness", pole: "high" }],
  },
  {
    cardId: "empathie", name: "Empathie", colour: "gruen",
    higherOrder: "self_transcendence", schwartz: ["benevolence", "universalism"],
    bigFive: [{ trait: "agreeableness", pole: "high" }],
  },
  {
    cardId: "zugehoerigkeit", name: "Zugehörigkeit", colour: "gruen",
    higherOrder: "conservation", schwartz: ["security"],
    deviatesFromColourPrior: true,
    rationale: "'Zugehörigkeitsgefühl' ist in Schwartz ein Sicherheits-Item — Abweichung von Grün.",
  },
  {
    cardId: "familie", name: "Familie", colour: "gruen",
    higherOrder: "conservation", schwartz: ["security", "benevolence"],
    deviatesFromColourPrior: true, contestable: true,
    rationale: "'Familiäre Sicherheit' ist das Prototyp-Sicherheits-Item; als Beziehungswert zugleich Benevolenz. Primär Sicherheit → Abweichung von Grün.",
  },
  { cardId: "zaertlichkeit", name: "Zärtlichkeit", colour: "gruen", higherOrder: "self_transcendence", schwartz: ["benevolence"] },
  { cardId: "freundschaft", name: "Freundschaft", colour: "gruen", higherOrder: "self_transcendence", schwartz: ["benevolence"] },
  {
    cardId: "harmonie", name: "Harmonie", colour: "gruen",
    higherOrder: "self_transcendence", schwartz: ["universalism", "security"],
    contestable: true,
    rationale: "'Innere Harmonie'/'Welt in Frieden' = Universalismus; relationale Harmonie grenzt an Sicherheit. Strittig.",
  },
  {
    cardId: "fuersorge", name: "Fürsorge", colour: "gruen",
    higherOrder: "self_transcendence", schwartz: ["benevolence"],
    bigFive: [{ trait: "agreeableness", pole: "high" }],
  },
  { cardId: "treue", name: "Treue", colour: "gruen", higherOrder: "self_transcendence", schwartz: ["benevolence"] },
  { cardId: "nachhaltigkeit", name: "Nachhaltigkeit", colour: "gruen", higherOrder: "self_transcendence", schwartz: ["universalism"] },
  {
    cardId: "toleranz", name: "Toleranz", colour: "gruen",
    higherOrder: "self_transcendence", schwartz: ["universalism"],
    bigFive: [{ trait: "agreeableness", pole: "high" }],
  },
  {
    cardId: "demut", name: "Demut", colour: "gruen",
    higherOrder: "conservation", schwartz: ["tradition"],
    deviatesFromColourPrior: true, contestable: true,
    rationale: "Demut/Bescheidenheit ist ein Traditions-Item ('meinen Platz im Leben annehmen'); in der revidierten Theorie ein eigener Wert zwischen Conservation und Self-Transcendence. Abweichung von Grün; strittig.",
  },
  {
    cardId: "intuition", name: "Intuition", colour: "gruen",
    higherOrder: "openness_to_change", schwartz: ["self_direction"],
    deviatesFromColourPrior: true, contestable: true,
    rationale: "Intuition = eigenem inneren Urteil folgen → Selbstbestimmung. Abweichung von Grün; strittig.",
  },
  {
    cardId: "mitgefuehl", name: "Mitgefühl", colour: "gruen",
    higherOrder: "self_transcendence", schwartz: ["benevolence", "universalism"],
    bigFive: [{ trait: "agreeableness", pole: "high" }],
  },
  {
    cardId: "herzlichkeit", name: "Herzlichkeit", colour: "gruen",
    higherOrder: "self_transcendence", schwartz: ["benevolence"],
    bigFive: [{ trait: "agreeableness", pole: "high" }],
  },

  // ── Gelb · Erfüllung / Freiheit (prior: openness_to_change) ──
  {
    cardId: "individualitaet", name: "Individualität", colour: "gelb",
    higherOrder: "openness_to_change", schwartz: ["self_direction"],
    bigFive: [{ trait: "openness", pole: "high" }],
  },
  {
    cardId: "spiritualitaet", name: "Spiritualität", colour: "gelb",
    higherOrder: "conservation", schwartz: ["tradition", "universalism"],
    deviatesFromColourPrior: true, contestable: true,
    rationale: "Spiritualität → Tradition (Sinn/Andacht), sekundär Universalismus; Schwartz' ursprünglicher 'Spiritualität'-Wert war nicht stabil. Abweichung von Gelb; strittig.",
  },
  { cardId: "authentizitaet", name: "Authentizität", colour: "gelb", higherOrder: "openness_to_change", schwartz: ["self_direction"] },
  { cardId: "unabhaengigkeit", name: "Unabhängigkeit", colour: "gelb", higherOrder: "openness_to_change", schwartz: ["self_direction"] },
  {
    cardId: "selbstverwirklichung", name: "Selbstverwirklichung", colour: "gelb",
    higherOrder: "openness_to_change", schwartz: ["self_direction", "achievement"],
    rationale: "Selbstverwirklichung → Selbstbestimmung (eigene Ziele/Entfaltung), sekundär Leistung.",
  },
  { cardId: "freiheit", name: "Freiheit", colour: "gelb", higherOrder: "openness_to_change", schwartz: ["self_direction"] },
  {
    cardId: "abenteuer", name: "Abenteuer", colour: "gelb",
    higherOrder: "openness_to_change", schwartz: ["stimulation"],
    bigFive: [{ trait: "openness", pole: "high" }],
  },
  {
    cardId: "kreativitaet", name: "Kreativität", colour: "gelb",
    higherOrder: "openness_to_change", schwartz: ["self_direction"],
    bigFive: [{ trait: "openness", pole: "high" }],
  },
  {
    cardId: "aesthetik", name: "Ästhetik", colour: "gelb",
    higherOrder: "self_transcendence", schwartz: ["universalism"],
    deviatesFromColourPrior: true, contestable: true,
    rationale: "'Eine Welt der Schönheit' ist ein Universalismus-Item. Abweichung von Gelb; strittig (auch Hedonismus/Selbstbestimmung denkbar).",
  },
  {
    cardId: "freude", name: "Freude", colour: "gelb",
    higherOrder: "openness_to_change", schwartz: ["hedonism"],
    rationale: "Hedonismus (Genuss/Lebensfreude) grenzt an Selbstverstärkung; im Prior-Rahmen der Openness/Erfüllung.",
  },
  {
    cardId: "aufgeschlossenheit", name: "Aufgeschlossenheit", colour: "gelb",
    higherOrder: "openness_to_change", schwartz: ["self_direction", "universalism"],
    bigFive: [{ trait: "openness", pole: "high" }],
  },
  {
    cardId: "innovation", name: "Innovation", colour: "gelb",
    higherOrder: "openness_to_change", schwartz: ["self_direction", "stimulation"],
    bigFive: [{ trait: "openness", pole: "high" }],
  },
  {
    cardId: "offenheit", name: "Offenheit", colour: "gelb",
    higherOrder: "openness_to_change", schwartz: ["self_direction"],
    bigFive: [{ trait: "openness", pole: "high" }],
  },
  {
    cardId: "flexibilitaet", name: "Flexibilität", colour: "gelb",
    higherOrder: "openness_to_change", schwartz: ["self_direction", "stimulation"],
    bigFive: [{ trait: "openness", pole: "high" }],
    contestable: true,
    rationale: "Flexibilität/Anpassungsfähigkeit → Selbstbestimmung + Stimulation (Abwechslung); Primärzuordnung strittig.",
  },
  {
    cardId: "neugier", name: "Neugier", colour: "gelb",
    higherOrder: "openness_to_change", schwartz: ["self_direction"],
    bigFive: [{ trait: "openness", pole: "high" }],
  },
  {
    cardId: "leidenschaft", name: "Leidenschaft", colour: "gelb",
    higherOrder: "openness_to_change", schwartz: ["stimulation", "hedonism"],
    bigFive: [{ trait: "extraversion", pole: "high" }],
  },

  // ── Rot · Fortschritt / Status (prior: self_enhancement) ──
  {
    cardId: "ehre", name: "Ehre", colour: "rot",
    higherOrder: "self_enhancement", schwartz: ["power"],
    contestable: true,
    rationale: "Ehre = Ruf/öffentliches Ansehen → Macht (Prestige); auch Tradition/Konformität vertretbar. Strittig.",
  },
  {
    cardId: "entwicklung", name: "Entwicklung", colour: "rot",
    higherOrder: "self_enhancement", schwartz: ["achievement", "self_direction"],
    contestable: true,
    rationale: "Im Fortschritts-Kontext primär Leistung (Vorankommen); persönliche Entwicklung grenzt an Selbstbestimmung. Strittig.",
  },
  {
    cardId: "verantwortung", name: "Verantwortung", colour: "rot",
    higherOrder: "self_transcendence", schwartz: ["benevolence", "conformity"],
    deviatesFromColourPrior: true,
    rationale: "'Verantwortlich' ist ein Benevolenz-Item; Pflichterfüllung grenzt an Konformität. Abweichung von Rot.",
  },
  {
    cardId: "wachstum", name: "Wachstum", colour: "rot",
    higherOrder: "openness_to_change", schwartz: ["self_direction", "achievement"],
    deviatesFromColourPrior: true, contestable: true,
    rationale: "Persönliches Wachstum → Selbstbestimmung (Entfaltung), sekundär Leistung. Abweichung von Rot; strittig.",
  },
  { cardId: "einfluss", name: "Einfluss", colour: "rot", higherOrder: "self_enhancement", schwartz: ["power"] },
  { cardId: "erfolg", name: "Erfolg", colour: "rot", higherOrder: "self_enhancement", schwartz: ["achievement"] },
  {
    cardId: "respekt", name: "Respekt", colour: "rot",
    higherOrder: "self_enhancement", schwartz: ["power"],
    contestable: true,
    rationale: "'Respekt' im Status-Kontext = respektiert werden → Macht/soziale Anerkennung; 'Respekt vor anderen' wäre Universalismus. Strittig.",
  },
  {
    cardId: "leistung", name: "Leistung", colour: "rot",
    higherOrder: "self_enhancement", schwartz: ["achievement"],
    bigFive: [{ trait: "conscientiousness", pole: "high" }],
  },
  { cardId: "ansehen", name: "Ansehen", colour: "rot", higherOrder: "self_enhancement", schwartz: ["power"] },
  {
    cardId: "anerkennung", name: "Anerkennung", colour: "rot",
    higherOrder: "self_enhancement", schwartz: ["achievement", "power"],
    contestable: true,
    rationale: "Anerkennung für Kompetenz → Leistung; soziale Anerkennung → Macht. Strittig.",
  },
  {
    cardId: "beharrlichkeit", name: "Beharrlichkeit", colour: "rot",
    higherOrder: "self_enhancement", schwartz: ["achievement"],
    bigFive: [{ trait: "conscientiousness", pole: "high" }],
  },
  {
    cardId: "mut", name: "Mut", colour: "rot",
    higherOrder: "openness_to_change", schwartz: ["stimulation", "self_direction"],
    deviatesFromColourPrior: true, contestable: true,
    rationale: "'Wagemut' ist ein Stimulations-Item; Mut auch Selbstbestimmung. Abweichung von Rot; strittig.",
  },
  {
    cardId: "hingabe", name: "Hingabe", colour: "rot",
    higherOrder: "self_transcendence", schwartz: ["benevolence", "achievement"],
    deviatesFromColourPrior: true, contestable: true,
    rationale: "Panel-Korrektur (9-Modell-Adjudikation 2026-07-23, 5/9): Hingabe primär als selbstlose Zuwendung zu Menschen → Benevolenz; Werk-Hingabe (Leistung) bleibt sekundär. Abweichung von Rot.",
  },
  {
    cardId: "grosszuegigkeit", name: "Großzügigkeit", colour: "rot",
    higherOrder: "self_transcendence", schwartz: ["benevolence"],
    bigFive: [{ trait: "agreeableness", pole: "high" }],
    deviatesFromColourPrior: true,
    rationale: "Großzügigkeit ist ein Benevolenz-Item. Abweichung von Rot.",
  },
  {
    cardId: "fortschritt", name: "Fortschritt", colour: "rot",
    higherOrder: "self_enhancement", schwartz: ["achievement", "stimulation"],
    contestable: true,
    rationale: "Fortschritt → Leistung/Vorankommen, grenzt an Stimulation/Openness (Veränderung). Strittig.",
  },
  {
    cardId: "wertschaetzung", name: "Wertschätzung", colour: "rot",
    higherOrder: "self_transcendence", schwartz: ["benevolence", "universalism"],
    deviatesFromColourPrior: true,
    rationale: "Wertschätzung = andere anerkennen/würdigen → Benevolenz/Universalismus. Abweichung von Rot.",
  },
]

/** The catalog size — the deck the Kartenspiel reduces from (62 → 8, §3). */
export const VALUE_CARD_COUNT = VALUE_CARD_CATALOG.length

/** Lookup a card by its stable `cardId`, or `undefined` if not in the deck. */
export function valueCardById(cardId: string): ValueCard | undefined {
  return VALUE_CARD_CATALOG.find((c) => c.cardId === cardId)
}
